import React from "react";
import { useT } from "@/lib/LanguageContext";
import { useSiteCopy } from "@/lib/useSiteCopy";
import { useTenant } from "@/lib/TenantContext";
import { buildFooterModel, resolveBrandName } from "@/lib/siteLinks";
import { SOCIAL_ICONS } from "./socialIcons";

/**
 * Classic's footer.
 *
 * It used to hardcode the SEED tenant's identity — "Émilie Houde Naturopathe" in the
 * copyright line and in the logo's alt text, plus her personal Instagram and Facebook
 * profiles — so every practitioner on the Classic theme published another individual's
 * name and social accounts as their own. Brand and socials now come from the TENANT
 * record (`tenant.name` / `tenant.branding.*_url`), and a network the practice has not
 * filled in renders no icon at all rather than an empty one.
 *
 * The links themselves are the shared model (lib/siteLinks.js), which the five themed
 * footers render too — so the site's sitemap is derived once and cannot drift per theme.
 *
 * @param {object} [toggles] `tenant.section_toggles`; a section is listed unless === false.
 */
const Footer = ({ toggles = {} }) => {
  // siteCopy = dictionary + Site Content overrides + the tenant record (lib/useSiteCopy.js).
  const siteCopy = useSiteCopy();
  // "templates" is the namespace of the six site templates, Classic (t1_classic) included:
  // the brand placeholder is shared with the five themed footers, not duplicated per theme.
  const tt = useT("templates");
  const { tenant } = useTenant();
  const f = buildFooterModel({
    siteCopy,
    tenant,
    toggles,
    brand: resolveBrandName(tenant, tt("shared.brand.fallback")),
  });
  const { images } = siteCopy;

  return (
    <footer data-testid="site-footer" className="bg-eh-cream border-t border-eh-border-soft">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-4">
          <img src={images.logo} alt={f.brand} className="h-28 md:h-36 w-auto -ml-3" />
          {/* BUG-210: no dictionary sample any more (lib/content.js) — a practice's
              specialty and areas of expertise are facts, not sample copy, and Site Content
              has no way to reach this string at all until buildFooterModel/resolveSiteCopy
              resolve it (lib/siteLinks.js, lib/siteContent.js). Same guard idiom as the
              hero's specialty badge and credential line. */}
          {f.tagline && <p className="mt-5 text-eh-muted max-w-sm leading-relaxed">{f.tagline}</p>}
        </div>

        <div className="col-span-6 md:col-span-2">
          <div className="eyebrow">{f.headings.navigate}</div>
          <div className="mt-4 space-y-2">
            {f.sections.map((s) => (
              <a key={s.key} href={s.href} data-testid={`footer-section-${s.key}`} className="block hover:text-eh-sage-deep">
                {s.label}
              </a>
            ))}
          </div>
        </div>

        <div className="col-span-6 md:col-span-2">
          <div className="eyebrow">{f.headings.explore}</div>
          <div className="mt-4 space-y-2">
            {f.pages.map((p) => (
              <a key={p.key} href={p.href} data-testid={`footer-page-${p.key}`} className="block hover:text-eh-sage-deep">
                {p.label}
              </a>
            ))}
          </div>
        </div>

        <div className="col-span-6 md:col-span-2">
          <div className="eyebrow">{f.headings.contact}</div>
          <div className="mt-4 space-y-2 text-eh-ink">
            <a href={`mailto:${f.contact.email}`} className="hover:text-eh-sage-deep break-all block">
              {f.contact.email}
            </a>
            <div className="text-eh-muted">{f.contact.place}</div>
          </div>
        </div>

        {f.socials.length > 0 && (
          <div className="col-span-6 md:col-span-2">
            <div className="eyebrow">{f.headings.social}</div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {f.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.key];
                return (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-10 h-10 rounded-full border border-eh-border-soft flex items-center justify-center hover:bg-eh-ink hover:text-eh-bg transition"
                    data-testid={`footer-social-${s.key}`}
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-eh-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between flex-wrap gap-3 text-xs text-eh-muted">
          <div data-testid="footer-copyright">© {f.year} {f.brand} — {f.rights}</div>
          <div className="font-script text-lg text-eh-ochre-deep">{f.built}</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
